import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../assets/css/ContactTab.css';
import { FaMapMarkerAlt, FaPhoneAlt, FaClock, FaFacebook, FaLinkedin, FaInstagram } from 'react-icons/fa';
import { IoMail } from "react-icons/io5";
import Navbar from './Navbar';
import Footer from './Footer';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const ContactTab = () => {
  const phoneRegex = /^[0-9]{11}$/;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [nameError, setNameError] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleContactForm = async (e) => {
    e.preventDefault();

    // Form validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('All fields are required');
      return;
    }

    if (!phoneRegex.test(formData.phone)) {
      toast.error('Phone number must be exactly 11 digits');
      return;
    }

    if (/[0-9!@#$%^&*(),.?":{}|<>]/.test(formData.name)) {
      setNameError('Name must only contain letters and spaces');
      return;
    }

    setLoading(true);

    try {
      // ✅ Prepare EmailJS template parameters
      const templateParams = {
        name: formData.name,
        time: new Date().toLocaleString(),
        user_name: formData.name,
        user_email: formData.email,
        user_phone: formData.phone,
        message: formData.message,
      };

      // ✅ Send email using EmailJS
      const response = await emailjs.send(
        'service_8r9l6sr',         // 🔹 Your Service ID
        'template_dl6sp6g',         // 🔹 Replace with your Template ID
        templateParams,
        'pf2txmlPhud07pyFT'          // 🔹 Replace with your EmailJS Public Key
      );

      if (response.status === 200) {
        toast.success('Message sent successfully!');
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error('Failed to send message.');
      }

    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Something went wrong while sending the email.');
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, name: value });

    if (/[0-9!@#$%^&*(),.?":{}|<>]/.test(value)) {
      setNameError('Name must only contain letters and spaces');
    } else {
      setNameError('');
    }
  };

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: 'ease-out',
      once: true
    });
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleContactForm(event);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | TechSol Technologies</title>
        <meta name="description" content="Get in touch with TechSol Technologies for inquiries, support, or feedback." />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="contact-hero" data-aos="fade-up">
        <div className="hero-background">
          <div className="hero-glow"></div>
        </div>
        <div className="container">
          <div className="hero-content text-center">
            <h1 className="hero-title">
              Get In <span className="gradient-text">Touch</span>
            </h1>
            <p className="hero-subtitle">
              Ready to start your next project? Let's discuss how we can bring your ideas to life.
            </p>
            <div className="breadcrumb">
              <Link to="/" className="breadcrumb-link">Home</Link>
              <span className="breadcrumb-separator"> / </span>
              <span className="breadcrumb-active">Contact</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid" data-aos="fade-up" data-aos-delay="200">
            
            {/* Contact Form */}
            <div className="contact-form-card">
              <div className="form-header">
                <h3 className="form-title">Send us a Message</h3>
                <p className="form-subtitle">We'll get back to you within 24 hours</p>
              </div>

              <form onSubmit={handleContactForm} onKeyDown={handleKeyDown} className="contact-form">
                <div className="form-group">
                  <div className="input-container">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleNameChange}
                      className="form-input shadow-none border"
                      placeholder="Your Name"
                      required
                    />
                    <div className="input-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#666">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                  </div>
                  {nameError && <span className="error-message">{nameError}</span>}
                </div>

                <div className="form-group">
                  <div className="input-container">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-input shadow-none border"
                      placeholder="Your Email"
                      required
                    />
                    <div className="input-icon">
                      <IoMail />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="form-input shadow-none border"
                      placeholder="Your Phone"
                      required
                    />
                    <div className="input-icon">
                      <FaPhoneAlt />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-container textarea-container">
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="form-input shadow-none border form-textarea"
                      rows="5"
                      placeholder="Your Message"
                      required
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className={`submit-button ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="button-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info Card */}
            <div className="contact-info-card">
              <div className="info-header">
                <h3 className="info-title">Contact Information</h3>
                <p className="info-subtitle">Get in touch through any of these channels</p>
              </div>

              <div className="info-items">
                <div className="info-item" data-aos="fade-up" data-aos-delay="100">
                  <div className="info-icon-wrapper">
                    <FaMapMarkerAlt className="info-icon" />
                  </div>
                  <div className="info-content">
                    <h5 className="info-label">Our Location</h5>
                    <p className="info-text">10/C Main, Raiwind Rd, Block C Nawab Town Lahore, Pakistan</p>
                  </div>
                </div>

                <div className="info-item" data-aos="fade-up" data-aos-delay="200">
                  <div className="info-icon-wrapper">
                    <IoMail className="info-icon" />
                  </div>
                  <div className="info-content">
                    <h5 className="info-label">Email Address</h5>
                    <a href="mailto:info@techsoltech.com" className="info-link">
                      info@techsoltech.com
                    </a>
                  </div>
                </div>

                <div className="info-item" data-aos="fade-up" data-aos-delay="300">
                  <div className="info-icon-wrapper">
                    <FaPhoneAlt className="info-icon" />
                  </div>
                  <div className="info-content">
                    <h5 className="info-label">Phone Number</h5>
                    <Link to="https://wa.me/923193284118" target='blank' className="info-link">
                      +92 319 32 84 118
                    </Link>
                  </div>
                </div>

                <div className="info-item" data-aos="fade-up" data-aos-delay="400">
                  <div className="info-icon-wrapper">
                    <FaClock className="info-icon" />
                  </div>
                  <div className="info-content">
                    <h5 className="info-label">Working Hours</h5>
                    <p className="info-text">
                      Mon-Fri: 9:00 AM - 5:00 PM<br />
                      <span className="closed-text">Weekends: Closed</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="social-section" data-aos="fade-up" data-aos-delay="500">
                <h5 className="social-title">Follow Us</h5>
                <div className="social-links">
                  <a href="https://www.facebook.com/profile.php?id=61571234807734" target="_blank" rel="noopener noreferrer" className="social-link contact-social-link">
                    <FaFacebook />
                  </a>
                  <a href="https://www.linkedin.com/company/techsoltech/" target="_blank" rel="noopener noreferrer" className="social-link contact-social-link">
                    <FaLinkedin />
                  </a>
                  <a href="https://www.instagram.com/techsol_technologies" target="_blank" rel="noopener noreferrer" className="social-link contact-social-link">
                    <FaInstagram />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className="map-section" data-aos="fade-up" data-aos-delay="400">
            <div className="map-container">
              <div className="map-header">
                <h3 className="map-title">Find Us Here</h3>
                <p className="map-subtitle">Visit our office or get directions</p>
              </div>
              <div className="map-iframe-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3401.23456789!2d74.2345678!3d31.23456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39190483e4531234%3A0xabc123def456!2sNawab%20Town%2C%20Lahore%2C%20Punjab%2C%20Pakistan!5e0!3m2!1sen!2s!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Techsol Technologies Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ContactTab;
